<template>
  <div class="form-data-page">
    <!--
      ★ 未带 formId 时（比如从侧边栏菜单直接点「表单数据」进来）：
        不弹一句「缺少参数」就停住，而是直接给一个可搜索的表单选择器 ——
        否则这个菜单项就是个死胡同。
    -->
    <div v-if="!query.formId" class="fd-pick">
      <div class="fd-top">
        <div class="fd-top-l">
          <div class="fd-title">
            <el-button icon="ArrowLeft" text @click="goList">返回</el-button>
            <h2>表单数据</h2>
            <span class="fd-badge">未选择表单</span>
          </div>
          <p class="fd-sub">
            先选一张表单 —— 点下面任意一张卡片即可进入它的提交数据；
            也可以从<b>表单列表</b>里点某张表的「数据」直接进来。
          </p>
        </div>
        <div class="fd-top-r">
          <el-button icon="Refresh" @click="loadPickList">刷 新</el-button>
          <el-button type="primary" icon="Plus" @click="goList">去表单列表</el-button>
        </div>
      </div>

      <div class="fd-bar">
        <el-input
            v-model="pickKeyword"
            clearable
            placeholder="搜索表单名称 / 短码"
            style="width: 260px;margin-right: 10px"
            @keyup.enter="loadPickList"></el-input>
        <el-button type="primary" icon="Search" @click="loadPickList">查 询</el-button>
      </div>

      <div v-loading="pickLoading" class="fd-pick-wrap">
        <div v-if="pickList.length" class="fd-pick-grid">
          <div v-for="f in pickList" :key="f.id" class="fd-pick-card" @click="pickForm(f)">
            <div class="fp-t">{{ f.title || ('#' + f.id) }}</div>
            <div class="fp-m">
              <span class="fp-code">{{ f.formKey || '—' }}</span>
              <span>{{ f.submitCount || 0 }} 条提交</span>
            </div>
            <div class="fp-foot">
              <el-tag :type="formStatusTagType(f.status)" size="small" effect="plain">
                {{ formStatusText(f.status) }}
              </el-tag>
              <span class="fp-go">进入数据 →</span>
            </div>
          </div>
        </div>
        <div v-else-if="!pickLoading && pickFailed" class="fd-pick-empty">
          <el-icon><WarningFilled /></el-icon>
          <p>{{ pickError || '表单列表加载失败' }}</p>
          <el-button text type="primary" icon="RefreshRight" @click="loadPickList">重试</el-button>
        </div>
        <div v-else-if="!pickLoading" class="fd-pick-empty">
          <el-icon><FolderOpened /></el-icon>
          <p>没有可选的表单</p>
          <el-button text type="primary" @click="goList">去表单列表新建一张</el-button>
        </div>
      </div>
    </div>

    <template v-else>
    <!-- 表单信息头 -->
    <div class="fd-top">
      <div class="fd-top-l">
        <div class="fd-title">
          <el-button icon="ArrowLeft" text @click="goList">返回</el-button>
          <h2>{{ form.title || '表单数据' }}</h2>
          <span class="fd-badge">{{ form.formKey || '—' }}</span>
        </div>
        <p class="fd-sub">
          按提交时间倒序。上面一排数字<b>点一下就能筛</b> —— 比如点「待处理」只看还没处理的。
        </p>
      </div>
      <div class="fd-top-r">
        <el-button icon="Refresh" @click="refresh">刷 新</el-button>
        <el-button icon="TrendCharts" @click="goStat">数据统计</el-button>
        <el-button type="primary" icon="Download" :loading="exportLoading" @click="openExport">导出 Excel</el-button>
      </div>
    </div>

    <!-- 概览条：点数字即筛选该处理状态 -->
    <div class="fd-ov">
      <div class="fd-ov-item fd-ov-item--click" :class="{ 'is-active': isStatusActive(null) }" @click="pickStatus(null)">
        <b class="c-all">{{ ov.total }}</b><span>总提交</span>
      </div>
      <div class="fd-ov-item fd-ov-item--click" :class="{ 'is-active': isStatusActive(0) }" @click="pickStatus(0)">
        <b class="c-wait">{{ ov.pending }}</b><span>待处理</span>
      </div>
      <div class="fd-ov-item fd-ov-item--click" :class="{ 'is-active': isStatusActive(1) }" @click="pickStatus(1)">
        <b class="c-done">{{ ov.done }}</b><span>已处理</span>
      </div>
      <div class="fd-ov-item fd-ov-item--click" :class="{ 'is-active': isStatusActive(2) }" @click="pickStatus(2)">
        <b class="c-void">{{ ov.voided }}</b><span>已作废</span>
      </div>
      <div class="fd-ov-item">
        <b class="c-view">{{ ov.views }}</b><span>浏览量</span>
      </div>
      <div class="fd-ov-item">
        <b class="c-rate">{{ ov.rate }}</b><span>回收率</span>
      </div>
      <span class="fd-ov-tip">
        <el-icon><InfoFilled /></el-icon>
        概览随城市 / 学校 / 项目 / 日期 / 关键字联动，但<b>不受处理状态筛选影响</b>
      </span>
    </div>

    <!-- 筛选 -->
    <div class="fd-bar">
      <el-select
          v-model="query.cityId"
          clearable
          filterable
          placeholder="城市"
          style="width: 150px;margin-right: 10px">
        <el-option v-for="c in cityOptions" :key="c.id" :label="c.name" :value="c.id"></el-option>
      </el-select>
      <!-- ★ 学校已改为手写输入 → 按名称模糊筛选（不再从选项里选） -->
      <el-input
          v-model="query.schoolName"
          clearable
          placeholder="学校名称(模糊)"
          style="width: 170px;margin-right: 10px"
          @keyup.enter="search"></el-input>
      <el-input
          v-model="query.projectName"
          clearable
          placeholder="测试项目"
          style="width: 150px;margin-right: 10px"></el-input>
      <el-date-picker
          v-model="dateRange"
          type="daterange"
          value-format="YYYY-MM-DD"
          range-separator="~"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          style="margin-right: 10px"></el-date-picker>
      <el-select
          v-model="query.status"
          clearable
          placeholder="全部状态"
          style="width: 130px;margin-right: 10px">
        <el-option label="待处理" :value="0"></el-option>
        <el-option label="已处理" :value="1"></el-option>
        <el-option label="已作废" :value="2"></el-option>
      </el-select>
      <el-input
          v-model="query.keyword"
          clearable
          placeholder="姓名 / 手机号"
          style="width: 170px;margin-right: 10px"
          @keyup.enter="search"></el-input>
      <el-button type="primary" icon="Search" @click="search">查 询</el-button>
      <el-button icon="RefreshLeft" @click="reset">重 置</el-button>
    </div>

    <!-- 表格 -->
    <div class="fd-card">
      <el-table
          v-loading="listLoading"
        :data="tableData"
        border>
      <el-table-column type="index" width="55" label="#" align="center" fixed="left"/>
      <el-table-column width="160" label="提交时间" fixed="left">
        <template #default="scope">{{ scope.row.addTime || scope.row.createTime || '—' }}</template>
      </el-table-column>
      <el-table-column
          v-for="col in columns"
          :key="col.key"
          :min-width="columnWidth(col)"
          show-overflow-tooltip>
        <!-- ★ v3.17：条件显示字段在表头标注 —— 某些记录里这份数据本就不该出现，
             比较时不要一概而论。（不做静默过滤：口径拿不到就「显示但标注」。） -->
        <template #header>
          <span>{{ col.label }}</span>
          <el-tooltip v-if="col.cond" placement="top"
                      content="该字段配了显示条件：部分记录里本就不该出现这个值，统计/比较时请勿一概而论">
            <el-tag size="small" type="warning" effect="plain" class="fd-cond-tag">条件</el-tag>
          </el-tooltip>
        </template>
        <template #default="scope">
          <!-- 手写签名：值是一张图片 URL → 单元格里直接给缩略图（无值显示「—」） -->
          <el-image
              v-if="col.type === 'signature' && imageListOf(scope.row, col).length"
              :src="imageListOf(scope.row, col)[0]"
              :preview-src-list="imageListOf(scope.row, col)"
              fit="contain"
              class="fd-thumb"></el-image>
          <span v-else-if="col.type === 'signature'" class="df-empty">—</span>
          <template v-else>{{ displayValue(scope.row, col) }}</template>
        </template>
      </el-table-column>
      <el-table-column width="90" align="center" label="状态">
        <template #default="scope">
          <el-tag :type="statusTagType(scope.row.status)" size="small">
            {{ statusText(scope.row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column width="190" align="center" fixed="right" label="操作">
        <template #default="scope">
          <el-button size="small" text type="primary" @click="openDetail(scope.row)">详情</el-button>
          <el-button size="small" text type="primary" @click="openStatus(scope.row)">状态 / 备注</el-button>
        </template>
      </el-table-column>

      <!-- 空态出口：没数据时给「重置筛选 / 去表单列表」，不留死页；
           失败态与空态分开 —— 失败给「重试」，不把接口报错伪装成「暂无数据」 -->
      <template #empty>
        <div v-if="listFailed" class="fd-fail">
          <el-icon><WarningFilled /></el-icon>
          <p>{{ listError || '提交数据加载失败' }}</p>
          <el-button type="primary" icon="RefreshRight" @click="selectList">重试</el-button>
        </div>
        <div v-else class="fd-empty">
          <el-icon><FolderOpened /></el-icon>
          <p>暂无提交数据 —— 可能还没有人填写，或筛选条件太窄</p>
          <el-button text type="primary" @click="reset">重置筛选</el-button>
          <el-button text type="primary" @click="goList">去表单列表</el-button>
        </div>
      </template>
    </el-table>

    <div class="pagination">
      <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :page-sizes="[10, 20, 50, 100]"
          :current-page="query.pageIndex"
          :page-size="query.pageSize"
          :total="pageTotal"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"></el-pagination>
    </div>
    </div>
    </template>

    <!-- 详情抽屉 -->
    <el-drawer v-model="detailVisible" title="提交详情" size="520px">
      <div v-loading="detailLoading" class="detail-body">
        <div class="detail-meta">
          <div class="meta-item"><span class="meta-k">提交时间</span><span>{{ detail.addTime || detail.createTime || '—' }}</span></div>
          <div class="meta-item"><span class="meta-k">手机号</span><span>{{ maskPhone(detail.phone) }}</span></div>
          <div class="meta-item"><span class="meta-k">城市</span><span>{{ detail.cityName || '—' }}</span></div>
          <div class="meta-item"><span class="meta-k">来源</span><span>{{ detail.source || '—' }}</span></div>
          <div class="meta-item">
            <span class="meta-k">处理状态</span>
            <el-tag :type="statusTagType(detail.status)" size="small">{{ statusText(detail.status) }}</el-tag>
          </div>
          <div class="meta-item"><span class="meta-k">备注</span><span>{{ detail.remark || '—' }}</span></div>
        </div>
        <el-divider/>
        <div v-for="col in detailColumns" :key="col.key" class="detail-field">
          <div class="df-label">
            {{ col.label }}
            <el-tag v-if="col.cond" size="small" type="warning" effect="plain" class="fd-cond-tag">条件</el-tag>
          </div>
          <div class="df-value">
            <!-- 图片 / 手写签名：都按图片展示（签名值就是一张图片 URL） -->
            <template v-if="col.type === 'image' || col.type === 'signature'">
              <div v-if="imageList(col).length" class="df-images">
                <el-image
                    v-for="(img, ii) in imageList(col)"
                    :key="ii"
                    :src="img"
                    :preview-src-list="imageList(col)"
                    fit="cover"
                    class="df-img"></el-image>
              </div>
              <span v-else class="df-empty">无</span>
            </template>
            <template v-else>{{ displayValue(detail, col) || '无' }}</template>
          </div>
        </div>
        <el-divider/>
        <!-- 原始数据：排查「某个字段为什么没显示」时最有用 —— 这里看到的就是真正入库的内容 -->
        <div class="df-raw">
          <div class="df-raw-head">
            <span>原始提交数据（JSON）</span>
            <el-button size="small" text type="primary" icon="CopyDocument" @click="copyRaw">复制</el-button>
          </div>
          <pre class="df-raw-body">{{ rawJson }}</pre>
        </div>
      </div>
      <template #footer>
        <el-button @click="detailVisible = false">关 闭</el-button>
        <el-button type="primary" icon="EditPen" @click="openStatusFromDetail">修改状态 / 备注</el-button>
      </template>
    </el-drawer>

    <!-- 状态 / 备注 -->
    <el-dialog v-model="statusVisible" title="修改处理状态 / 备注" width="420px">
      <el-form label-width="80px">
        <el-form-item label="处理状态">
          <el-select v-model="statusForm.status" style="width: 100%">
            <el-option label="待处理" :value="0"></el-option>
            <el-option label="已处理" :value="1"></el-option>
            <el-option label="已作废" :value="2"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="备注">
          <el-input type="textarea" :rows="3" v-model="statusForm.remark" maxlength="200"></el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="statusVisible = false">取 消</el-button>
        <el-button type="primary" :loading="statusSaving" @click="submitStatus">确 定</el-button>
      </template>
    </el-dialog>

    <!-- 导出设置：自选列（字段多的表单不必每次都导一张几十列的表） -->
    <el-dialog v-model="exportDialog" title="导出设置" width="560px">
      <div class="export-head">
        <span class="export-tip">勾选要导出的字段；提交时间 / 手机号 / 处理状态 / 备注 始终导出</span>
        <div class="export-ops">
          <el-button size="small" text type="primary" @click="toggleExportAll(true)">全选</el-button>
          <el-button size="small" text @click="toggleExportAll(false)">清空</el-button>
        </div>
      </div>
      <el-checkbox-group v-model="exportCols" class="export-cols">
        <el-checkbox v-for="c in columns" :key="c.key" :label="c.key">{{ c.label }}</el-checkbox>
      </el-checkbox-group>
      <div v-if="!columns.length" class="export-tip">该表单没有可导出的字段</div>
      <template #footer>
        <el-button @click="exportDialog = false">取 消</el-button>
        <el-button type="primary" :loading="exportLoading" @click="doExport">开始导出</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  formPage,
  formDetail,
  formRecordPage,
  formRecordDetail,
  formRecordStatistics,
  formRecordStatus,
  formRecordExport,
  formCities,
  RECORD_STATUS_TEXT,
  FORM_STATUS_TEXT,
} from '@/api/form';

/**
 * 兼容「被全局 XSS 过滤器转义过的历史 JSON（schema / dataJson）」。
 * 后端框架（XssJacksonDeserializer）会把 @RequestBody 里所有 String 做
 * escapeHtml4()，JSON 文本里的 `"` 变成 `&quot;`，直接 JSON.parse 会失败。
 * 先替换具名实体、最后还原 `&amp;`，避免 `&amp;quot;` 被二次误还原。
 */
function htmlUnescape(s) {
  return s
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
}
function parseJsonLoose(v) {
  if (!v || typeof v !== 'string') return null;
  try {
    return JSON.parse(v);
  } catch (e) { /* 继续尝试反转义 */ }
  if (v.indexOf('&') < 0) return null;
  try {
    return JSON.parse(htmlUnescape(v));
  } catch (e) {
    return null;
  }
}

// 纯展示型字段：不进提交数据，也不作为数据列展示（与后端 DISPLAY_TYPES 保持一致）
//   v3.15 新增 richText（富文本说明：合同 / 协议 / 声明）——
//   ★ 它只在**表单定义**里存在（告诉填写人要看什么），提交数据里根本没有它的 key，
//     不在这里排除就会多出一列全是空白的「说明」。
const DISPLAY_TYPES = ['tips', 'section', 'divider', 'imgs', 'richText'];
// 「预留业务键」→ 提交记录的结构化镜像列，用于无 data 时的兜底取值
const MIRROR_MAP = {
  city: 'cityName',
  campus: 'campusName',
  school: 'schoolName',
  grade: 'grade',
  project: 'projectNames',
  studentName: 'studentName',
  sex: 'studentSex',
  phone: 'phone',
};

export default {
  name: 'FormData',
  data() {
    return {
      form: {},
      columns: [],
      cityOptions: [],
      tableData: [],
      pageTotal: 0,
      // 概览条口径：除「处理状态」外的全部筛选（这样点状态筛选时，概览不会塌成只剩一个状态）
      ov: { total: 0, pending: 0, done: 0, voided: 0, views: 0, rate: '—' },
      listLoading: false,
      listFailed: false,
      listError: '',
      exportLoading: false,
      dateRange: [],
      query: {
        formId: null,
        pageIndex: 1,
        pageSize: 10,
        cityId: null,
        schoolName: '',
        projectName: '',
        startDate: '',
        endDate: '',
        status: null,
        keyword: '',
      },
      detailVisible: false,
      detailLoading: false,
      detail: {},
      statusVisible: false,
      statusSaving: false,
      statusForm: { id: null, status: 0, remark: '' },
      // 导出设置：勾选的字段 key（空数组 = 未选，不允许导出）
      exportDialog: false,
      exportCols: [],
      // 未带 formId 时的「选择表单」面板
      pickLoading: false,
      pickFailed: false,
      pickError: '',
      pickList: [],
      pickKeyword: '',
    };
  },
  computed: {
    detailColumns() {
      return this.columns;
    },
    /** 详情抽屉里的「原始 JSON」——直接显示入库内容，排查字段缺失时最省事 */
    rawJson() {
      const d = this.detail || {};
      const raw = d.data || this.parseJson(d.dataJson) || {};
      try {
        return JSON.stringify(raw, null, 2);
      } catch (e) {
        return '{}';
      }
    },
  },
  methods: {
    statusText(status) {
      return RECORD_STATUS_TEXT[status] || '待处理';
    },
    /** 手机号脱敏：11 位手机号中间四位打码；其它格式原样返回，空值统一给「—」 */
    maskPhone(phone) {
      const s = String(phone == null ? '' : phone);
      if (/^\d{11}$/.test(s)) return s.slice(0, 3) + '****' + s.slice(7);
      return s || '—';
    },
    /**
     * 表单状态文案 / 标签色（「选择表单」卡片用）。
     * ★ 必须按 0/1/2 三档翻 —— 原来写成「status===1 ? 收集中 : 草稿」，
     *   把「已结束」也显示成「草稿」，与 FormList / FormSetting 的口径直接矛盾。
     */
    formStatusText(status) {
      return FORM_STATUS_TEXT[status] || FORM_STATUS_TEXT[0];
    },
    formStatusTagType(status) {
      if (status === 1) return 'success';
      if (status === 2) return 'danger';
      return 'info';
    },
    statusTagType(status) {
      if (status === 1) return 'success';
      if (status === 2) return 'danger';
      return 'warning';
    },
    columnWidth(col) {
      // 签名/文件/排序题内容偏长（图片、文件数、按序拼接的中文名）→ 与 textarea/image 同宽
      // ★ v3.9：级联选择的展示值是「层级路径」（如 浙江省/杭州市/西湖区），同样偏长
      // ★ v3.15：扫一扫的值就是二维码原文（多半是一整条 URL），120px 会被截得只剩开头
      if (['textarea', 'image', 'signature', 'file', 'sort', 'cascader', 'scanCode'].includes(col.type)) return 180;
      if (['school'].includes(col.type)) return 140;
      if (['daterange', 'region', 'location'].includes(col.type)) return 180;
      if (['switch', 'rate', 'slider', 'time', 'date'].includes(col.type)) return 110;
      return 120;
    },
    goList() {
      this.$router.push({ path: '/formList' });
    },
    /** 跳转该表单的数据统计（按城市/学校/测试项目/日期聚合） */
    goStat() {
      this.$router.push({ path: '/formStat', query: { formId: this.query.formId } });
    },
    /** 手动刷新：列表与概览一起重取 */
    refresh() {
      this.selectList();
    },
    /** 点概览数字 → 按该处理状态筛选（null = 全部）；页码回到第 1 页 */
    pickStatus(s) {
      this.query.status = s;
      this.query.pageIndex = 1;
      this.selectList();
    },
    /** 概览项高亮判定（el-select 清空后可能是 null 也可能是 undefined，两种都算「全部」） */
    isStatusActive(s) {
      if (s === null) return this.query.status === null || this.query.status === undefined;
      return this.query.status === s;
    },
    /**
     * 概览条：复用统计接口，但只传「除 status 外」的筛选条件。
     *
     * <p>为什么排除 status：统计接口的 byStatus 用的就是同一套筛选，
     * 如果带上 status，三个状态数字会塌成「只剩被选中的那一个」，
     * 用户就没法在「待处理 ↔ 已处理」之间跳着看了。</p>
     */
    loadOverview() {
      if (!this.query.formId) return;
      this.syncDateRange();
      const param = { formId: this.query.formId };
      if (this.query.cityId != null) param.cityId = this.query.cityId;
      if (this.query.schoolName) param.schoolName = this.query.schoolName;
      if (this.query.projectName) param.projectName = this.query.projectName;
      if (this.query.keyword) param.keyword = this.query.keyword;
      if (this.query.startDate) param.startDate = this.query.startDate;
      if (this.query.endDate) param.endDate = this.query.endDate;
      formRecordStatistics(param).then(val => {
        if (!val || val.code !== 200 || !val.data) return;
        const d = val.data;
        const st = {};
        (Array.isArray(d.byStatus) ? d.byStatus : []).forEach(it => {
          if (!it) return;
          st[String(it.name)] = Number(it.cnt || 0);
        });
        const total = Number(d.total || 0);
        const views = Number(d.viewCount || 0);
        this.ov = {
          total,
          pending: st['0'] || 0,
          done: st['1'] || 0,
          voided: st['2'] || 0,
          views,
          rate: views > 0 ? (Math.round((total * 1000) / views) / 10) + '%' : '—',
        };
      }).catch(() => { /* 概览只是增强展示，取不到不能影响主列表 */ });
    },
    copyRaw() {
      this.copyText(this.rawJson, '已复制原始 JSON');
    },
    /** 从详情抽屉直接改状态（省掉「关抽屉 → 回列表点按钮」两步） */
    openStatusFromDetail() {
      const row = this.detail;
      this.detailVisible = false;
      this.openStatus(row);
    },
    /** 与 FormList 同款：clipboard API + textarea 兜底（老浏览器/非 https 环境） */
    copyText(text, tip) {
      const done = () => this.$message.success(tip || '已复制');
      const fallback = () => {
        try {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          done();
        } catch (e) {
          this.$message.warning('复制失败，请手动复制');
        }
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(fallback);
      } else {
        fallback();
      }
    },
    /* ---------- 未带 formId：给一张表单清单让用户挑 ---------- */
    /**
     * 拉表单清单（只读）。
     * 这里**不弹**「缺少参数」的告警 —— 页面本身已经渲染了选择器，
     * 弹告警反而像是操作失败。
     */
    loadPickList() {
      this.pickLoading = true;
      this.pickFailed = false;
      this.pickError = '';
      formPage({ pageIndex: 1, pageSize: 100, keyword: this.pickKeyword || '' }).then(val => {
        this.pickLoading = false;
        if (val && val.code === 200) {
          this.pickList = (val.data && val.data.records) || [];
        } else {
          this.pickList = [];
          this.pickFailed = true;
          this.pickError = (val && val.message) || '表单列表加载失败';
        }
      }).catch((err) => {
        this.pickLoading = false;
        this.pickList = [];
        this.pickFailed = true;
        this.pickError = (err && err.message) || '表单列表加载失败，请检查网络';
      });
    },
    /** 选中某张表单 → 就地加载它的数据，并把 formId 写回地址栏（刷新后仍停在同一条） */
    pickForm(f) {
      if (!f || f.id == null) return;
      this.query.formId = f.id;
      this.query.pageIndex = 1;
      // 同一路由只换 query 不会重建组件，所以这里手动接管加载，不依赖 created()
      this.$router.replace({ path: '/formData', query: { formId: f.id } });
      this.loadForm();
      this.selectList();
    },
    /* ---------- 表单结构 ---------- */
    loadForm() {
      // 未选表单时由上面的选择器接管，这里安静返回即可
      if (!this.query.formId) return;
      formDetail(this.query.formId).then(val => {
        if (!val || val.code !== 200 || !val.data) {
          // 拿不到结构 ⇒ 数据列会只剩「时间 / 状态」，不静默：明确告知原因
          this.columns = [];
          this.$message.warning((val && val.message) || '表单结构加载失败，数据列可能不完整');
          return;
        }
        const d = val.data;
        const f = d.form || d;
        const schema = d.schema || this.parseJson(f.schemaJson) || {};
        this.form = f;
        const fields = Array.isArray(schema.fields) ? schema.fields : [];
        this.columns = fields
            .filter(x => !DISPLAY_TYPES.includes(x.type))
            .map(x => ({
              key: x.key,
              label: x.label,
              type: x.type,
              unit: x.unit || '',
              // ★ v3.17：是否配了「显示条件」—— 表头标注用（不改口径，只做提示）
              cond: !!(x.visibleRule && Array.isArray(x.visibleRule.conds) && x.visibleRule.conds.length),
              // 矩阵题展示需要 rows/cols 做「值 → 中文标签」翻译（其余类型用不到）
              rows: x.type === 'matrix' ? (x.rows || []) : undefined,
              cols: x.type === 'matrix' ? (x.cols || []) : undefined,
              // 图片单选存的是 option.value，表格/详情要按 options 翻成中文 label
              options: x.type === 'imageSelect' ? (x.options || []) : undefined,
            }));
      }).catch((err) => {
        this.columns = [];
        this.$message.error((err && err.message) || '表单结构加载失败，数据列可能不完整');
      });
    },
    parseJson(v) {
      if (!v) return null;
      if (typeof v === 'object') return v;
      return parseJsonLoose(v);
    },
    cellRaw(row, col) {
      const data = row.data || this.parseJson(row.dataJson) || {};
      if (data && Object.prototype.hasOwnProperty.call(data, col.key)) {
        return data[col.key];
      }
      const mirror = MIRROR_MAP[col.key];
      if (mirror && row[mirror] != null && row[mirror] !== '') return row[mirror];
      return '';
    },
    displayValue(row, col) {
      const v = this.cellRaw(row, col);
      // ★ 手机号一律脱敏（与 FormVerify 同口径）—— 表格列与详情字段都走这里
      if (col && col.type === 'phone') return this.maskPhone(v);
      if (v === null || v === undefined || v === '') return '';
      // 排序题：值已是「按用户排定顺序」的 [{label,value}] → 依序拼 label
      // ⚠️ 必须排在下面的通用数组分支之前，否则会退化成「、」连接（丢失顺序语义）
      if (col && col.type === 'sort' && Array.isArray(v)) {
        return v
            .map(it => (it && typeof it === 'object' ? (it.label || it.value || '') : it))
            .filter(Boolean)
            .join(' → ');
      }
      if (Array.isArray(v)) {
        // 文件上传（v3.5）：值是文件 URL 数组 → 表格/详情只给数量，
        // ⚠️ 不能落到下面的「、」拼接分支，否则单元格会变成一长串 https:// 糊屏
        if (col && col.type === 'file') {
          const n = v.filter(x => !!x).length;
          return n ? ('共 ' + n + ' 个文件') : '';
        }
        // ★ v3.15 录音：与 file 同形（音频直链数组），同一个坑、同一套解法。
        //   文案刻意不用「个文件」—— 导出/表格里要一眼看出这是语音而不是附件。
        if (col && col.type === 'audio') {
          const n = v.filter(x => !!x).length;
          return n ? ('已录 ' + n + ' 段语音') : '';
        }
        return v.map(item => (item && typeof item === 'object' ? (item.name || item.label || '') : item)).join('、');
      }
      // 矩阵题：对象 {行值: 列值} → 按 schema 的 rows/cols 翻成「行标签：列标签；…」
      // ⚠️ 必须放在下面的 typeof === 'object' 兜底之前，否则会走 JSON.stringify 显示成原始对象
      if (col && col.type === 'matrix') {
        return this.matrixText(col, v);
      }
      if (typeof v === 'object') {
        return v.name || v.label || JSON.stringify(v);
      }
      // 开关：Boolean → 是 / 否（否则会显示成 true / false）
      if (typeof v === 'boolean') return v ? '是' : '否';
      // 评分：数字 → n 星
      if (col && col.type === 'rate') {
        const n = Number(v);
        return (isNaN(n) || n <= 0) ? '' : n + ' 星';
      }
      // 滑块：带单位展示
      if (col && col.type === 'slider') {
        return String(v) + (col.unit || '');
      }
      // 图片单选：存的是 option.value，展示要按 options 翻成中文 label
      //（与 radio/select 的「选项值 → 选项名」同口径）
      if (col && col.type === 'imageSelect') {
        return this.optionLabel(col, v);
      }
      return String(v);
    },
    /**
     * 选项值 → 中文标签：imageSelect 的值是 option.value，表格/详情都要翻成 label 才可读。
     * 命中不了就原样返回 —— 宁可显示原始值，也不要因为翻译失败把「有值」显示成空白。
     */
    optionLabel(col, value) {
      const opts = (col && col.options) || [];
      const hit = opts.find(o => o && String(o.value) === String(value));
      return (hit && hit.label) || String(value);
    },
    /**
     * 矩阵题展示："行标签：列标签；行标签：列标签"
     * ★ 与后端 matrixText / 小程序 recordDetail#_mxText 三处同口径：
     *   按 schema 的 rows 顺序输出，不是对象的遍历顺序。
     */
    matrixText(col, v) {
      if (!v || typeof v !== 'object' || Array.isArray(v)) return '';
      const rows = (col && col.rows) || [];
      const cols = (col && col.cols) || [];
      const out = [];
      rows.forEach(r => {
        const rv = (r && r.value !== undefined && r.value !== null && r.value !== '') ? r.value : (r && r.label);
        const cv = v[rv];
        if (cv === undefined || cv === null || cv === '') return;
        const hit = cols.find(c => String((c.value === undefined || c.value === '') ? c.label : c.value) === String(cv));
        out.push(`${(r && r.label) || rv}：${hit ? (hit.label || cv) : cv}`);
      });
      return out.join('；');
    },
    /** 从任意一条记录里取「图片型字段」（image / signature）的 URL 列表 */
    imageListOf(row, col) {
      const v = this.cellRaw(row, col);
      if (!v) return [];
      if (Array.isArray(v)) return v.filter(Boolean);
      if (typeof v === 'string') return v.split(',').filter(Boolean);
      return [];
    },
    /** 详情抽屉用：取当前 detail 的图片列表（保持原有调用签名 imageList(col) 不变） */
    imageList(col) {
      return this.imageListOf(this.detail, col);
    },
    /* ---------- 列表 ---------- */
    search() {
      this.query.pageIndex = 1;
      this.selectList();
    },
    reset() {
      this.query.cityId = null;
      this.query.schoolName = '';
      this.query.projectName = '';
      this.query.status = null;
      this.query.keyword = '';
      this.dateRange = [];
      this.syncDateRange();
      this.query.pageIndex = 1;
      this.selectList();
    },
    handleSizeChange(size) {
      this.query.pageSize = size;
      this.query.pageIndex = 1;
      this.selectList();
    },
    handlePageChange(index) {
      this.query.pageIndex = index;
      this.selectList();
    },
    syncDateRange() {
      if (Array.isArray(this.dateRange) && this.dateRange.length === 2) {
        this.query.startDate = this.dateRange[0];
        this.query.endDate = this.dateRange[1];
      } else {
        this.query.startDate = '';
        this.query.endDate = '';
      }
    },
    selectList() {
      this.syncDateRange();
      this.listLoading = true;
      this.listFailed = false;
      this.listError = '';
      // 概览与列表并发请求，互不阻塞
      this.loadOverview();
      formRecordPage(this.query).then(val => {
        this.listLoading = false;
        if (val && val.code === 200) {
          this.tableData = (val.data && val.data.records) || [];
          this.pageTotal = (val.data && val.data.total) || 0;
        } else {
          this.tableData = [];
          this.pageTotal = 0;
          this.listFailed = true;
          this.listError = (val && val.message) || '提交数据加载失败';
        }
      }).catch((err) => {
        this.listLoading = false;
        this.tableData = [];
        this.pageTotal = 0;
        this.listFailed = true;
        this.listError = (err && err.message) || '提交数据加载失败，请检查网络';
      });
    },
    /* ---------- 详情 ---------- */
    openDetail(row) {
      this.detailVisible = true;
      this.detail = row;
      this.detailLoading = true;
      formRecordDetail(row.id).then(val => {
        this.detailLoading = false;
        if (val && val.code === 200 && val.data) {
          this.detail = val.data;
        } else {
          this.$message.warning((val && val.message) || '详情加载失败，已显示列表里的概要数据');
        }
      }).catch((err) => {
        this.detailLoading = false;
        this.$message.error((err && err.message) || '详情加载失败，已显示列表里的概要数据');
      });
    },
    /* ---------- 状态 / 备注 ---------- */
    openStatus(row) {
      this.statusForm = {
        id: row.id,
        status: row.status == null ? 0 : row.status,
        remark: row.remark || '',
      };
      this.statusVisible = true;
    },
    submitStatus() {
      this.statusSaving = true;
      formRecordStatus(this.statusForm).then(val => {
        this.statusSaving = false;
        if (val && val.code === 200) {
          this.$message.success('已更新');
          this.statusVisible = false;
          this.selectList();
        } else {
          this.$message.warning((val && val.message) || '更新失败');
        }
      }).catch((err) => {
        this.statusSaving = false;
        this.$message.error((err && err.message) || '更新失败，请稍后重试');
      });
    },
    /* ---------- 导出 ---------- */
    /** 打开导出设置（默认全选，用户可取消不想要的列） */
    openExport() {
      this.exportCols = this.columns.map(c => c.key);
      this.exportDialog = true;
    },
    toggleExportAll(all) {
      this.exportCols = all ? this.columns.map(c => c.key) : [];
    },
    doExport() {
      if (!this.exportCols.length) {
        this.$message.warning('请至少勾选一个字段');
        return;
      }
      this.exportDialog = false;
      this.exportExcel();
    },
    exportExcel() {
      this.syncDateRange();
      // ★ 导出列：只勾了一部分才传 exportKeys（后端按 schema 顺序输出）；
      //   全选 = 不传，保持与「导出全列」完全一致的老行为。
      const all = this.columns.map(c => c.key);
      const picked = Array.isArray(this.exportCols) ? this.exportCols : [];
      this.query.exportKeys = (picked.length && picked.length < all.length) ? picked.slice() : [];
      this.exportLoading = true;
      formRecordExport(this.query).then(val => {
        this.exportLoading = false;
        if (val && val.code === 200) {
          const url = val.data && val.data.url;
          const rows = (val.data && val.data.rows) || 0;
          if (url) {
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            link.setAttribute('download', (val.data && val.data.name) || `${this.form.title || '表单数据'}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            this.$message.success(`导出成功（${rows} 条）`);
          } else {
            this.$message.success('导出成功，请到右上角下载中心查看！');
          }
        } else {
          this.$message.warning((val && val.message) || '导出失败');
        }
      }).catch(() => {
        this.exportLoading = false;
        this.$message.warning('导出失败');
      });
    },
    loadCityOptions() {
      formCities().then(val => {
        if (val && val.code === 200 && Array.isArray(val.data)) {
          this.cityOptions = val.data
              .filter(n => n && n.id != null)
              .map(n => ({ id: n.id, name: n.name }));
        }
      });
    },
  },
  created() {
    this.query.formId = this.$route.query.formId || null;
    // 支持从统计页「按学校分布」点条形下钻：带 schoolName 直接预置筛选
    this.query.schoolName = this.$route.query.schoolName || '';
    this.loadCityOptions();
    if (this.query.formId) {
      this.loadForm();
      this.selectList();
    } else {
      this.loadPickList();
    }
  },
};
</script>

<style scoped>

/* @form-tokens:start */
/* ======================= 表单模块设计令牌（v3.11 视觉统一）=======================
 * ★ 本块在 9 个表单页各存一份（CSS 变量按作用域隔离，无法跨 scoped 共享）。
 *   改配色的唯一入口 = D:/tmp/shot/form_tokens.py 的 TOKEN_BLOCK，改完重跑该脚本
 *   一键同步；**不要手改单页**，否则 9 份会漂移。
 * ★ 为什么不放全局 :root：--el-color-primary 一旦全局生效会波及后台所有模块
 *   （订单 / 教练 / 财务…）。本模块只对「表单 9 页」负责，故挂在页面根容器上，
 *   作用域精确、零外溢。
 * ========================================================================== */
.form-data-page {
  /* —— Element 主色对齐「焱火绿」——
     此前后台从未覆盖 --el-color-primary，Element 一直是默认**蓝色**，
     而页面自定义元素用 #8dc63f ⇒ 同屏「按钮蓝 + 品牌绿」两个颜色。
     这 7 个变量一改，本页所有 el-button / el-switch / el-radio / el-checkbox /
     el-slider / el-pagination / focus 描边… 一次性全部变绿。
     light-3/5/7/8/9 与 dark-2 必须一并给出，否则 hover / disabled 态会
     回退到 Element 基于**蓝色**算出的浅色，出现「绿底 + 蓝边」的跳变。 */
  --el-color-primary: #8dc63f;
  --el-color-primary-light-3: #afd779;
  --el-color-primary-light-5: #c6e39f;
  --el-color-primary-light-7: #ddeec5;
  --el-color-primary-light-8: #e8f4d9;
  --el-color-primary-light-9: #f4f9ec;
  --el-color-primary-dark-2: #719e32;

  /* —— 文字五档（严格对齐 Element 灰阶；置成令牌后同类文字不会再各写一个灰） —— */
  --yh-t1: #303133; /* 主文字 */
  --yh-t2: #606266; /* 常规 */
  --yh-t3: #909399; /* 次要 */
  --yh-t4: #a8abb2; /* 占位 */
  --yh-t5: #c0c4cc; /* 禁用 */

  /* —— 主色四档 —— */
  --yh-theme: #8dc63f;
  --yh-theme-d: #7cad37;
  --yh-theme-dd: #5f9427;
  --yh-theme-l: #eef7e2;
  --yh-theme-ll: #f6fbef;

  /* —— 面与线 —— */
  --yh-bg: #f5f7fa;
  --yh-fill: #fafbfc;
  --yh-line: #ebeef5;
  --yh-line-d: #dcdfe6;
  --yh-divide: #f2f3f5;

  /* —— 圆角五档 —— */
  --yh-r-xs: 4px;
  --yh-r-sm: 6px;
  --yh-r: 8px;
  --yh-r-lg: 12px;
  --yh-r-pill: 999px;
}
/* @form-tokens:end */
.form-data-page {
  padding: 16px 20px 30px;
  min-height: 100%;
  background-color: #f5f7fa;
  box-sizing: border-box;
}

/* ==================== 顶部 ==================== */
.fd-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 14px 20px 16px;
  margin-bottom: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fd-title {
  display: flex;
  align-items: center;
  gap: 6px;
}

.fd-title h2 {
  margin: 0;
  font-size: 19px;
  font-weight: 600;
  color: var(--yh-t1);
}

.fd-badge {
  padding: 1px 9px;
  border-radius: var(--yh-r-pill);
  background: var(--yh-theme-l);
  color: var(--yh-theme-dd);
  font-size: 12px;
  font-family: Consolas, Monaco, monospace;
}

.fd-sub {
  margin: 6px 0 0;
  max-width: 720px;
  font-size: 12px;
  line-height: 1.8;
  color: var(--yh-t3);
}

.fd-sub b {
  color: var(--yh-t2);
}

.fd-top-r {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex: 0 0 auto;
}

/* ==================== 未选表单时的选择器 ==================== */
.fd-pick-wrap {
  min-height: 220px;
}

.fd-pick-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
}

.fd-pick-card {
  cursor: pointer;
  padding: 14px 16px 12px;
  border-radius: var(--yh-r-lg);
  background: #fff;
  border: 1px solid var(--yh-line);
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.05);
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
}

.fd-pick-card:hover {
  border-color: #b8e07a;
  box-shadow: 0 4px 14px rgba(141, 198, 63, 0.16);
  transform: translateY(-1px);
}

.fp-t {
  font-size: 15px;
  font-weight: 600;
  color: var(--yh-t1);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.fp-m {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  font-size: 12px;
  color: var(--yh-t3);
}

.fp-code {
  padding: 1px 7px;
  border-radius: var(--yh-r-pill);
  background: #f2f3f5;
  font-family: Consolas, Monaco, monospace;
  color: var(--yh-t2);
}

.fp-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed #f0f1f3;
}

.fp-go {
  font-size: 12px;
  color: var(--yh-theme);
  opacity: 0;
  transition: opacity 0.15s;
}

.fd-pick-card:hover .fp-go {
  opacity: 1;
}

.fd-pick-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 48px 0;
  border-radius: var(--yh-r-lg);
  background: #fff;
  color: var(--yh-t4);
  font-size: 13px;
}

.fd-pick-empty .el-icon {
  font-size: 34px;
  color: #d8dade;
}

.fd-pick-empty p {
  margin: 0;
}

/* 表格空态：图标 + 文案 + 出口按钮（与 fd-pick-empty 不同 class，避免同文件覆盖） */
.fd-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 24px 0 10px;
}

.fd-empty .el-icon {
  font-size: 32px;
  color: #dcdfe6;
}

.fd-empty p {
  margin: 0;
  font-size: 13px;
  color: var(--yh-t4);
}

/* 表格失败态：与空态分开 —— 图标橙色 + 重试按钮，不把报错伪装成「暂无数据」 */
.fd-fail {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 26px 0 12px;
}

.fd-fail .el-icon {
  font-size: 32px;
  color: #e6a23c;
}

.fd-fail p {
  margin: 0;
  font-size: 13px;
  color: var(--yh-t4);
}

/* ==================== 概览条（可点筛选） ==================== */
.fd-ov {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 10px 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fd-ov-item {
  display: flex;
  align-items: baseline;
  gap: 4px;
  padding: 4px 10px;
  border-radius: var(--yh-r);
  background: var(--yh-fill);
  border: 1px solid transparent;
}

.fd-ov-item b {
  font-size: 15px;
  color: var(--yh-t1);
}

.fd-ov-item span {
  font-size: 12px;
  color: var(--yh-t3);
}

.fd-ov-item--click {
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.fd-ov-item--click:hover {
  background: #f2f9e8;
}

.fd-ov-item--click.is-active {
  background: var(--yh-theme-l);
  border-color: #b8e07a;
}

.fd-ov-tip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  font-size: 12px;
  color: var(--yh-t4);
}

.fd-ov-tip b {
  color: var(--yh-t2);
  font-weight: 600;
}

.c-all { color: var(--yh-t1) !important; }
.c-wait { color: #e6a23c !important; }
.c-done { color: #67c23a !important; }
.c-void { color: #c8c9cc !important; }
.c-view { color: #2f6bd8 !important; }
.c-rate { color: var(--yh-theme) !important; }

/* ==================== 筛选 ==================== */
.fd-bar {
  background: #fff;
  padding: 12px 16px;
  margin-bottom: 12px;
  border-radius: var(--yh-r-lg);
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

/* ==================== 表格卡 ==================== */
.fd-card {
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 6px 12px 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fd-card :deep(.el-table__inner-wrapper::before) {
  display: none;
}

.fd-card :deep(.el-table__header th) {
  background: var(--yh-fill);
  color: var(--yh-t2);
  font-weight: 600;
}

.fd-card :deep(.el-table__row:hover > td) {
  background: var(--yh-fill) !important;
}

.pagination {
  margin-top: 16px;
  text-align: right;
}

/* 详情 */
.detail-body {
  padding: 0 4px;
}
.detail-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 16px;
}
.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--yh-t1);
}
.meta-k {
  color: var(--yh-t3);
  flex: 0 0 60px;
}
.detail-field {
  margin-bottom: 14px;
}
.df-label {
  font-size: 12px;
  color: var(--yh-t3);
  margin-bottom: 4px;
}
.df-value {
  font-size: 14px;
  color: var(--yh-t1);
  word-break: break-all;
}
.df-empty {
  color: var(--yh-t5);
}
/* ★ v3.17：条件显示字段的表头标注 */
.fd-cond-tag {
  margin-left: 4px;
  transform: scale(0.85);
}
.df-images {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.df-img {
  width: 90px;
  height: 90px;
  border-radius: var(--yh-r-sm);
  border: 1px solid var(--yh-line);
}

/* 表格里的手写签名缩略图（与详情大图 .df-img 区分：同文件内 class 不得重名） */
.fd-thumb {
  width: 46px;
  height: 46px;
  border-radius: var(--yh-r-xs);
  border: 1px solid var(--yh-line);
  vertical-align: middle;
  background: var(--yh-fill);
}

/* 原始 JSON：排查「某个字段为什么没显示」时看的就是这里 */
.df-raw {
  margin-top: 4px;
}

.df-raw-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: var(--yh-t3);
}

.df-raw-body {
  margin: 6px 0 0;
  max-height: 260px;
  overflow: auto;
  padding: 10px 12px;
  border-radius: var(--yh-r);
  background: var(--yh-fill);
  color: var(--yh-t2);
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-all;
}

/* ---------------- 导出设置弹窗 ---------------- */
.export-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.export-tip {
  font-size: 12px;
  color: var(--yh-t3);
  line-height: 1.6;
}

.export-ops {
  flex: 0 0 auto;
  margin-left: 12px;
}

.export-cols {
  max-height: 320px;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2px 12px;
  padding: 6px 2px;
  border-top: 1px solid var(--yh-divide);
}

.export-cols :deep(.el-checkbox) {
  margin-right: 0;
  overflow: hidden;
}

.export-cols :deep(.el-checkbox__label) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
